// src/components/EditAppointment.js

import React, { useEffect, useState } from 'react';
import { db } from '../../firebaseConfig';
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
} from 'firebase/firestore';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useTenant } from '../../contexts/TenantContext';
import {
  buildActivityText,
  createActivityLog,
  deleteAppointmentWithActivityLogs,
} from '../../utils/adminAppointments';

const EditAppointment = ({ appointmentId, onClose, onAppointmentsChanged }) => {
  const [appointment, setAppointment] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const { tenantId } = useTenant();

  useEffect(() => {
    if (!tenantId) return;

    const fetchAppointment = async () => {
      try {
        const docRef = doc(db, 'tenants', tenantId, 'appointments', appointmentId);
        const appDoc = await getDoc(docRef);
        if (appDoc.exists()) {
          setAppointment({ id: appDoc.id, ...appDoc.data() });
          console.log('Termin geladen:', { id: appDoc.id, ...appDoc.data() });
        } else {
          setError('Termin nicht gefunden.');
        }
      } catch (err) {
        console.error(err);
        setError('Fehler beim Abrufen des Termins.');
      } finally {
        setLoading(false);
      }
    };

    const fetchEmployeesAndTypes = async () => {
      try {
        // Mitarbeiter abrufen
        const employeesRef = collection(db, 'tenants', tenantId, 'employees');
        const employeesSnapshot = await getDocs(employeesRef);
        const emps = employeesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setEmployees(emps);
        console.log('Mitarbeiter geladen:', emps);

        // Terminarten abrufen
        const typesRef = collection(db, 'tenants', tenantId, 'appointmentTypes');
        const typesSnapshot = await getDocs(typesRef);
        const types = typesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setAppointmentTypes(types);
        console.log('Terminarten geladen:', types);
      } catch (err) {
        console.error(err);
        setError('Fehler beim Abrufen der Mitarbeiter oder Terminarten.');
      }
    };

    const fetchData = async () => {
      setLoading(true);
      await fetchAppointment();
      await fetchEmployeesAndTypes();
    };

    fetchData();
  }, [tenantId, appointmentId]);

  const handleUpdate = async () => {
    // Plausibilitätscheck
    if (
      !appointment.customerName ||
      !appointment.date ||
      !appointment.startTime ||
      !appointment.duration ||
      !appointment.employeeId ||
      !appointment.appointmentTypeId
    ) {
      setError('Alle Felder sind erforderlich.');
      return;
    }

    // Validierung der Zeitformate
    const timeRegex = /^([0-1]\d|2[0-3]):([0-5]\d)$/;
    if (!timeRegex.test(appointment.startTime)) {
      setError('Startzeit muss im Format HH:MM sein.');
      return;
    }

    if (isNaN(Number(appointment.duration)) || Number(appointment.duration) <= 0) {
      setError('Dauer muss eine positive Zahl sein.');
      return;
    }

    try {
      const appointmentRef = doc(db, 'tenants', tenantId, 'appointments', appointmentId);
      await updateDoc(appointmentRef, {
        customerName: appointment.customerName,
        customerEmail: appointment.customerEmail || '',
        customerPhoneNumber: appointment.customerPhoneNumber || '',
        date: appointment.date,
        startTime: appointment.startTime,
        duration: Number(appointment.duration), // Dauer als Zahl speichern
        employeeId: appointment.employeeId,
        appointmentTypeId: appointment.appointmentTypeId,
      });
      await createActivityLog(tenantId, {
        type: 'appointment_updated',
        appointmentId,
        appointmentDate: appointment.date,
        startTime: appointment.startTime,
        customerName: appointment.customerName,
        employeeId: appointment.employeeId,
        appointmentTypeId: appointment.appointmentTypeId,
        message: buildActivityText({
          type: 'appointment_updated',
          customerName: appointment.customerName,
        }),
      });
      setMessage('Termin erfolgreich aktualisiert.');
      setError(null);
      console.log('Termin aktualisiert:', {
        customerName: appointment.customerName,
        date: appointment.date,
        startTime: appointment.startTime,
        duration: Number(appointment.duration),
        employeeId: appointment.employeeId,
        appointmentTypeId: appointment.appointmentTypeId,
      });

      // Liste im übergeordneten Komponent aktualisieren
      onAppointmentsChanged?.();
      // Optional: Formular schließen
      onClose?.();
    } catch (err) {
      console.error(err);
      setError('Fehler beim Aktualisieren des Termins.');
    }
  };

  const handleCancel = async () => {
    if (window.confirm('Möchten Sie diesen Termin wirklich stornieren?')) {
      try {
        const appointmentRef = doc(db, 'tenants', tenantId, 'appointments', appointmentId);
        await updateDoc(appointmentRef, {
          canceled: true,
          canceledAt: new Date(),
          canceledBy: 'admin',
        });
        await createActivityLog(tenantId, {
          type: 'appointment_canceled',
          appointmentId,
          appointmentDate: appointment.date,
          startTime: appointment.startTime,
          customerName: appointment.customerName,
          employeeId: appointment.employeeId,
          appointmentTypeId: appointment.appointmentTypeId,
          message: buildActivityText({
            type: 'appointment_canceled',
            customerName: appointment.customerName,
            appointmentDate: appointment.date,
            startTime: appointment.startTime,
          }),
        });
        setMessage('Termin erfolgreich storniert.');
        console.log('Termin storniert:', appointmentId);
        setAppointment(null);

        // Liste im übergeordneten Komponent aktualisieren
        onAppointmentsChanged?.();
        // Optional: Formular schließen
        onClose?.();
      } catch (err) {
        console.error(err);
        setError('Fehler beim Stornieren des Termins.');
      }
    }
  };

  const handleDeletePermanently = async () => {
    const confirmed = window.confirm(
      'Diesen Termin endgültig löschen? Der Eintrag wird vollständig aus Firestore entfernt und kann nicht wiederhergestellt werden.'
    );
    if (!confirmed) return;

    try {
      await deleteAppointmentWithActivityLogs(tenantId, appointmentId);
      setMessage('Termin endgültig gelöscht.');
      setAppointment(null);
      onAppointmentsChanged?.();
      onClose?.();
    } catch (err) {
      console.error(err);
      setError('Fehler beim endgültigen Löschen des Termins.');
    }
  };

  if (loading) {
    return <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />;
  }
  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }
  if (!appointment) {
    // Termin wurde gelöscht oder nicht gefunden
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">Termin wurde gelöscht oder nicht gefunden.</Alert>
        <Button variant="outlined" onClick={onClose}>
          Schließen
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" gutterBottom>
        Termin bearbeiten
      </Typography>

      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="Kundenname"
          value={appointment.customerName}
          onChange={(e) =>
            setAppointment((prev) => ({ ...prev, customerName: e.target.value }))
          }
        />
        <TextField
          label="E-Mail"
          type="email"
          value={appointment.customerEmail || ''}
          onChange={(e) =>
            setAppointment((prev) => ({ ...prev, customerEmail: e.target.value }))
          }
        />
        <TextField
          label="Telefon"
          type="tel"
          value={appointment.customerPhoneNumber || ''}
          onChange={(e) =>
            setAppointment((prev) => ({ ...prev, customerPhoneNumber: e.target.value }))
          }
        />
        <TextField
          label="Datum"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={appointment.date}
          onChange={(e) =>
            setAppointment((prev) => ({ ...prev, date: e.target.value }))
          }
        />
        <TextField
          label="Startzeit"
          type="time"
          InputLabelProps={{ shrink: true }}
          value={appointment.startTime}
          onChange={(e) =>
            setAppointment((prev) => ({ ...prev, startTime: e.target.value }))
          }
        />
        <TextField
          label="Dauer (Minuten)"
          type="number"
          InputLabelProps={{ shrink: true }}
          value={appointment.duration}
          onChange={(e) =>
            setAppointment((prev) => ({ ...prev, duration: e.target.value }))
          }
        />
        {/* Mitarbeiter-Auswahl */}
        <FormControl fullWidth>
          <InputLabel>Mitarbeiter</InputLabel>
          <Select
            value={appointment.employeeId}
            onChange={(e) =>
              setAppointment((prev) => ({ ...prev, employeeId: e.target.value }))
            }
          >
            <MenuItem value="">
              <em>Wähle einen Mitarbeiter</em>
            </MenuItem>
            {employees.map((emp) => (
              <MenuItem key={emp.id} value={emp.id}>
                {emp.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Terminarten */}
        <FormControl fullWidth>
          <InputLabel>Terminart</InputLabel>
          <Select
            value={appointment.appointmentTypeId}
            onChange={(e) =>
              setAppointment((prev) => ({
                ...prev,
                appointmentTypeId: e.target.value,
              }))
            }
          >
            <MenuItem value="">
              <em>Wähle eine Terminart</em>
            </MenuItem>
            {appointmentTypes.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {type.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mt: 4, flexWrap: 'wrap' }}>
        <Button variant="contained" color="primary" onClick={handleUpdate}>
          Aktualisieren
        </Button>
        <Button variant="outlined" color="warning" onClick={handleCancel}>
          Stornieren
        </Button>
        <Button variant="contained" color="error" onClick={handleDeletePermanently}>
          Endgültig löschen
        </Button>
        <Button variant="outlined" onClick={onClose}>
          Schließen
        </Button>
      </Box>
    </Box>
  );
};

export default EditAppointment;
